/**
 * Listing title pattern resolve — default simple map + rich overrides (D13).
 *
 * Most subcategories use the default simple slot fill (size → brand → model → product).
 * Listing-family adapters are rich overrides: they supply extra slots (inverter, form, …)
 * but still compose through {@link composeListingTitleFromSlots} (Phase 2+).
 */

import { normalizeCompactCapacityInText } from "@/lib/listing/sizeTextWithUnit";
import {
  composeListingTitleFromSlots,
  type ListingTitleSlotValues,
} from "@/lib/listing/listingTitleTokens";

export type ListingTitlePatternKind = "simple" | "rich";

/**
 * Optional explicit rich overrides by subcategory id.
 * Phase 1: family runtime remains the primary rich signal via
 * {@link resolveListingTitlePatternKind}'s `hasRichAdapter` option.
 * This set is for tests and future non-runtime registrations.
 */
const RICH_TITLE_OVERRIDE_IDS = new Set<string>();

/** Register a subcategory as a rich title override (tests / Phase 2+). */
export function registerRichListingTitleOverride(subcategoryId: string): void {
  const id = subcategoryId.trim();
  if (id) RICH_TITLE_OVERRIDE_IDS.add(id);
}

/** Test helper — clear explicit rich overrides. */
export function clearRichListingTitleOverridesForTests(): void {
  RICH_TITLE_OVERRIDE_IDS.clear();
}

export function hasRichListingTitleOverride(subcategoryId: string): boolean {
  return RICH_TITLE_OVERRIDE_IDS.has(subcategoryId);
}

/**
 * Resolve pattern kind. Default is **simple**.
 * Pass `hasRichAdapter: true` when a listing-family compose adapter exists.
 */
export function resolveListingTitlePatternKind(
  subcategoryId?: string | null,
  options?: { hasRichAdapter?: boolean },
): ListingTitlePatternKind {
  if (options?.hasRichAdapter) return "rich";
  if (subcategoryId && RICH_TITLE_OVERRIDE_IDS.has(subcategoryId)) {
    return "rich";
  }
  return "simple";
}

export type SimpleListingTitleInput = {
  size?: string | null;
  brand?: string | null;
  model?: string | null;
  /** Product noun — usually subcategory display name. */
  product?: string | null;
};

/** Build default simple slot values (D2 / D10 / D13). */
export function buildSimpleListingTitleSlots(
  input: SimpleListingTitleInput,
): ListingTitleSlotValues {
  const size = input.size?.trim();
  const brand = input.brand?.trim();
  const model = input.model?.trim();
  const product = input.product?.trim();

  return {
    capacity: size ? normalizeCompactCapacityInText(size) : undefined,
    brand: brand || undefined,
    model: model || undefined,
    product: product || undefined,
  };
}

/** Compose via default simple map through the shared engine. */
export function composeSimpleListingTitle(
  input: SimpleListingTitleInput,
): string {
  return composeListingTitleFromSlots(buildSimpleListingTitleSlots(input));
}
